import React, { useEffect, useState } from "react";
import { PageHeader, Modal, Toast, EmptyState, ExportButtons } from "@/components/Layout";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ViewOnly from "@/components/ViewOnly";
import { postImport } from "@/lib/importJob";
import { lock } from "@/lib/perm";
import PurgeButton from "@/components/PurgeButton";
import RowActions from "@/components/RowActions";
import ImportFileStep from "@/components/ImportFileStep";
import Select from "@/components/Select";
import useFilters from "@/lib/useFilters";
import { FilterForm } from "@/components/FilterActions";
import { useNavigate, useSearchParams } from "react-router-dom";
import RegesFields, { useRegesNomenclature } from "@/components/RegesFields";
import SeniorityFields, { seniorityFormState, seniorityPayload } from "@/components/SeniorityFields";
import EmploymentEvents from "@/components/EmploymentEvents";
import CustomFields, { useCustomDefs } from "@/components/CustomFields";
import { downloadFile } from "@/lib/download";
import FormTabs, { tabStyle } from "@/components/FormTabs";

const CO_FIELDS = [
  ["co_year_available", "CO an de programare"],
  ["co_carried_previous", "CO reportat din anii anteriori"],
  ["co_expires_june30", "din care CO care expiră la 30.06"],
  ["co_extra_hazardous", "CO suplimentar condiții vătămătoare"],
  ["co_extra_other", "CO suplimentar alte situații"],
];
const STATUS_LABELS = { active: "Activ", suspended: "Suspendat", terminated: "Încetat" };
const STATUS_BADGE_CLASS = { active: "badge-active", suspended: "badge-submitted", terminated: "badge-inactive" };
const POST_SELECTABLE_STATUSES = ["vacant", "temporar_vacant"];
// Câmpuri didactice — apar doar dacă modulul Stat de Funcții Didactic e licențiat.
const MEDICAL_LEVELS = { "": "— nespecificat —", rezident: "Rezident", specialist: "Specialist", primar: "Primar" };
const DOCTORAL_STATUSES = { "": "— niciuna —", student_doctorand: "Student doctorand", doctor: "Doctor în științe" };
const OCCUPATION_MODES = { "": "— nespecificat —", titular: "Titular", suplinitor: "Suplinitor" };
const DIDACTIC_FUNCTIONS = { "": "— nespecificată —", profesor: "Profesor universitar", conferentiar: "Conferențiar universitar", sef_lucrari: "Șef de lucrări universitar", asistent: "Asistent universitar" };
const EMPTY_DIDACTIC = {
  didactic_function: "", medical_level: "", medical_specialty: "", doctoral_status: "", doctoral_domain: "", phd_supervisor: false,
  occupation_mode: "", management_norm_reduction_percent: 0,
};
const fmtMonths = (m) => (m === null || m === undefined) ? "—" : `${Math.floor(m / 12)} ani ${m % 12} luni`;
// vechimea ca „N ani M luni”, din obiectul calculat de server ({years, months}); gol dacă persoana nu are data de început
const fmtTenure = (t) => t ? `${t.years} ani ${t.months} luni` : "—";

export default function Employments() {
  const { hasModule, has } = useAuth();
  const planCoLicensed = hasModule("planco");
  const statFunctiiLicensed = hasModule("stat_functii");
  const didacticLicensed = hasModule("stat_didactic");
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customDefs = useCustomDefs("employment");
  const [cfVals, setCfVals] = useState({});         // valorile câmpurilor personalizate ale raportului: { cheie: valoare }
  const [inherited, setInherited] = useState([]);   // valori primite de la angajat (doar citire)
  const [pay, setPay] = useState(null);             // { base_salary, currency } — null = fără dreptul data.view_salary
  const [payDirty, setPayDirty] = useState(false);
  const [senInfo, setSenInfo] = useState(null);      // răspunsul /mdm/employments/seniority (data de referință, primul CIM)
  const [senForm, setSenForm] = useState(null);      // vechimile din formular (se salvează pe angajat)
  const nom = useRegesNomenclature();
  const [tab, setTab] = useState("general");
  const [jdInfo, setJdInfo] = useState({ exists: false, postHas: false });
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [posts, setPosts] = useState([]);
  const [functions, setFunctions] = useState([]);
  // filtre cu „Aplică filtre”: implicit se afișează CIM active și suspendate; cele încetate doar când filtrezi după status
  const ef = useFilters({ marca: "", last: "", first: "", unit: "", status: "activ_susp" });
  const fa = ef.applied;
  const [employer, setEmployer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [msg, setMsg] = useState({text:"",type:"info"});

  const load = async () => {
    try {
      // fiecare sursă se încarcă separat: dacă una lipsește (fără drept), lista de rapoarte tot apare
      const soft = (req, fallback) => req.catch(() => ({ data: fallback }));
      const calls = [
        soft(api.get("/mdm/employments"), []), soft(has("users.list") ? api.get("/users") : Promise.resolve({ data: [] }), []), soft(api.get("/mdm/org-units"), []),
        soft(api.get("/mdm/locations"), []), soft(api.get("/config"), null), soft(api.get("/mdm/employer"), null),
        soft(has("functions.view") ? api.get("/mdm/functions") : Promise.resolve({ data: [] }), []),
      ];
      // (ultimul element din calls e funcțiile; posturile, dacă sunt licențiate, vin după)
      if (statFunctiiLicensed && has("stat_functii.view")) calls.push(soft(api.get("/stat-functii/posts"), []));
      const [e,u,o,l,c,emp,fn,p] = await Promise.all(calls);
      setFunctions(fn.data);
      setRows(e.data); setUsers(u.data); setUnits(o.data); setLocations(l.data); setCfg(c.data); setEmployer(emp.data);
      if (p) setPosts(p.data);
    } finally {
      setLoading(false);
    }
  };
  // statFunctiiLicensed depinde de AuthContext.licenses, încărcat asincron —
  // dacă pagina montează înainte ca acesta să se rezolve, re-rulăm load()
  // când valoarea se schimbă, altfel lista de posturi ar rămâne goală definitiv.
  useEffect(() => { load(); }, [statFunctiiLicensed]);

  const newE = () => setEditing({
    user_id: "", marca:"", org_unit_id: "",
    position_title:"", stat_function_id:"", internal_function_id:"", cor_function_id:"", salarizare_function_id:"",
    function_type:"executie", mgmt_internal_function_id:"", mgmt_cor_function_id:"", mgmt_salarizare_function_id:"",
    propagate_internal:false, propagate_cor:false, propagate_salarizare:false,
    propagate_mgmt_internal:false, propagate_mgmt_cor:false, propagate_mgmt_salarizare:false, location_id: "", personnel_category: (cfg?.personnel_categories||["TESA"])[0],
    contract_number:"", start_date: new Date().toISOString().slice(0,10), end_date: "",
    reges_contract_type:"ContractIndividualMunca", reges_duration:"Nedeterminata", reges_norm_type:"NormaIntreaga", reges_norm:"NormaIntreaga840",
    co_year_available: 21,
    co_carried_previous: 0, co_expires_june30: 0, co_extra_hazardous: 0, co_extra_other: 0,
    manager_employment_id: "", status: "active", status_reason: "",
    studii: (cfg?.education_levels||[])[0] || "", gradatie: "",
    cumul_pensie: false, replaces_employment_id: "", post_id: "",
    ...(didacticLicensed ? { didactic: { ...EMPTY_DIDACTIC } } : {}),
  });

  useEffect(() => { setTab("general"); }, [editing?.id, !!editing]);
  useEffect(() => {                                       // starea fișei de post, când se deschide fila „Documente”
    if (tab !== "documente" || !editing?.id) return;
    api.get(`/stat-functii/job-descriptions/employment/${editing.id}`).then(r => setJdInfo({ exists: r.data.exists, postHas: !!r.data.post_has_description })).catch(() => setJdInfo({ exists: false, postHas: false }));
  }, [tab, editing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {                                       // valorile câmpurilor personalizate și salariul raportului deschis
    setCfVals({}); setInherited([]); setPay(null); setPayDirty(false);
    if (!editing) return;
    const canPay = has("data.view_salary");
    if (!editing.id) { if (canPay) setPay({ base_salary: "", currency: "RON" }); return; }
    api.get(`/custom-fields/employment/${editing.id}`).then(r => {
      const o = {}; r.data.own.forEach(x => { if (!x.masked) o[x.key] = x.value; });
      setCfVals(o); setInherited(r.data.inherited);
    }).catch(() => {});
    if (canPay) api.get(`/mdm/employments/${editing.id}/pay`).then(r => setPay({ base_salary: r.data.base_salary ?? "", currency: r.data.currency })).catch(() => setPay(null));
  }, [editing?.id, !!editing]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {                                       // vechimile angajatului ales (și data de început, pentru „primul CIM”)
    setSenInfo(null); setSenForm(null);
    if (!editing?.user_id) return;
    let alive = true;
    api.get("/mdm/employments/seniority", { params: { user_id: editing.user_id, exclude_id: editing.id || undefined } }).then(r => {
      if (!alive) return; setSenInfo(r.data); setSenForm(seniorityFormState(r.data));
    }).catch(() => {});
    return () => { alive = false; };
  }, [editing?.user_id, editing?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // primul CIM = cel mai vechi dintre rapoartele salvate și cel din formular
  const senInfoLive = senInfo && editing ? {
    ...senInfo,
    default_employer_start: [senInfo.default_employer_start, editing.start_date].filter(Boolean).sort()[0] || null,
  } : null;

  // „+ Raport de muncă nou” din fișa unui angajat: formularul se deschide cu angajatul ales
  const personParam = searchParams.get("person");
  useEffect(() => {
    if (!personParam || !users.length || !cfg) return;
    const u = users.find(x => x.person_id === personParam);
    if (u && has("employments.create")) { newE(); setEditing(prev => ({ ...prev, user_id: u.id })); }
    setSearchParams({}, { replace: true });
  }, [personParam, users.length, !!cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!editing.user_id) { setTab("general"); return setMsg({text:"Selectează utilizatorul",type:"error"}); }
    if (!editing.marca?.trim()) { setTab("general"); return setMsg({text:"Marca este obligatorie",type:"error"}); }
    if (!editing.org_unit_id) { setTab("general"); return setMsg({text:"Selectează structura organizatorică",type:"error"}); }
    if ((editing._link_post ?? !!editing.post_id) && !editing.replaces_employment_id && !editing.post_id)
      { setTab("general"); return setMsg({text:"Alege postul din Stat de funcții sau debifează legarea de un post",type:"error"}); }
    const { _link_post, ...rest } = editing;
    const body = {
      ...rest,
      gradatie: editing.gradatie === "" || editing.gradatie === null ? null : parseInt(editing.gradatie, 10),
      replaces_employment_id: editing.replaces_employment_id || null,
      post_id: (editing._link_post ?? !!editing.post_id) ? (editing.post_id || null) : null,
      stat_function_id: editing.stat_function_id || null,
      internal_function_id: editing.internal_function_id || null,
      cor_function_id: editing.cor_function_id || null,
      salarizare_function_id: editing.salarizare_function_id || null,
      mgmt_internal_function_id: editing.mgmt_internal_function_id || null,
      mgmt_cor_function_id: editing.mgmt_cor_function_id || null,
      mgmt_salarizare_function_id: editing.mgmt_salarizare_function_id || null,
      function_type: editing.function_type || "executie",
      end_date: editing.end_date || null,
    };
    // `seniority` din rând e doar de afișat (calculat de server pentru SFD): vechimile se trimit din formular
    delete body.seniority;
    if (senForm) body.seniority = seniorityPayload(senForm, didacticLicensed);
    // Fără licență nu trimitem deloc câmpurile didactice (backend-ul le-ar refuza oricum cu 402).
    if (!didacticLicensed) delete body.didactic;
    try {
      let res = null, eid = editing.id;
      if (editing.id) res = (await api.put(`/mdm/employments/${editing.id}`, body)).data;
      else eid = (await api.post("/mdm/employments", body)).data.id;
      if (Object.keys(cfVals).length) await api.put(`/custom-fields/employment/${eid}`, { values: cfVals });
      if (pay && payDirty && has("data.view_salary")) await api.put(`/mdm/employments/${eid}/pay`, { base_salary: pay.base_salary === "" ? null : Number(pay.base_salary), currency: pay.currency });
      const r = editing.id ? res : null;
      const note = r?.scheduled ? ` Schimbarea de status a fost programată pentru ${fmtDate(editing.status_effective_date)}.`
        : r?.user_deactivated ? " Contul utilizatorului a fost inactivat (nu mai are alt CIM activ)."
        : r?.user_reactivated ? " Contul utilizatorului a fost reactivat." : "";
      setEditing(null); load(); setMsg({text:"Salvat."+note,type:"success"}); setTimeout(()=>setMsg({text:""}), note ? 5000 : 1500);
    } catch(e){ setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  // <Select> dintr-un nomenclator; păstrează afișată valoarea deja salvată chiar dacă a fost dezactivată între timp.
  const fnSelect = (field, kind, label, testid, showCode, propagateField) => {
    const cur = editing?.[field] || "";
    // lista COR poate avea mii de intrări — în aceeași casetă se scrie denumirea sau codul (vezi components/Select.jsx)
    const items = functions.filter(f => f.kind === kind);
    const known = items.some(f => f.id === cur);
    const text = f => showCode ? `${f.name} — cod ${f.code}` : f.name;
    return (
      <div style={showCode ? {gridColumn:"1/-1"} : undefined}><label className="label">{label}</label>
        <Select className="select" value={cur} onChange={e=>setEditing({...editing,[field]:e.target.value})} data-testid={testid}>
          <option value="">— nealeasă —</option>
          {cur && !known && <option value={cur}>(valoare dezactivată)</option>}
          {items.map(f=><option key={f.id} value={f.id}>{text(f)}</option>)}
        </Select>
        {functions.filter(f => f.kind === kind).length === 0 && <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Lista e goală — adaug-o din „Funcții și coduri COR".</div>}
        {propagateField && (
          <label style={{display:"flex",gap:6,alignItems:"center",fontSize:12,marginTop:4,cursor:cur?"pointer":"not-allowed",opacity:cur?1:0.55}}>
            <input type="checkbox" disabled={!cur} checked={!!editing[propagateField] && !!cur} data-testid={`${testid}-propagate`}
              onChange={e=>setEditing({...editing,[propagateField]:e.target.checked})} />
            Se propagă în salarizare și REGES
          </label>
        )}
      </div>
    );
  };

  const downloadTemplate = () => {
    fetch(`${API}/import/employments/template`, { credentials: "include" })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(b);
        a.download = "template_rapoarte_munca.xlsx"; a.click();
      });
  };
  // aruncă la eroare: mesajul exact al serverului îl afișează ImportFileStep sub butonul „Importă”
  const uploadFile = async (file, onProgress) => {
    const r = await postImport("/import/employments", file, onProgress);
    setImportResult(r.data); load();
  };

  const fold = (x) => String(x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const subtree = (rootId) => {                       // structura aleasă include și subunitățile ei
    const out = new Set([rootId]); let grew = true;
    while (grew) { grew = false; units.forEach(u => { if (u.parent_id && out.has(u.parent_id) && !out.has(u.id)) { out.add(u.id); grew = true; } }); }
    return out;
  };
  const unitSet = fa.unit ? subtree(fa.unit) : null;
  const statusOk = (e) => fa.status === "all" ? true : fa.status === "activ_susp" ? e.status !== "terminated" : e.status === fa.status;
  const filteredRows = rows.filter(e => statusOk(e)
    && (!unitSet || unitSet.has(e.org_unit_id))
    && (!fa.marca || fold(e.marca).includes(fold(fa.marca)))
    && (!fa.last || fold(e.last_name).includes(fold(fa.last)))
    && (!fa.first || fold(e.first_name).includes(fold(fa.first))));
  const unitOptions = [...units].filter(u => u.active).sort((x, y) => (x.name || "").localeCompare(y.name || "", "ro"));
  const fmtDate = (d) => d ? String(d).slice(0, 10).split("-").reverse().join(".") : "";

  return (
    <>
      <PageHeader title="Rapoarte de Muncă" subtitle={`${rows.filter(r=>r.status!=="terminated").length} mărci active`}
        actions={<>
          <ExportButtons basePath="/mdm/employments" perm="employments.export" />
          <button className="btn btn-secondary" onClick={downloadTemplate} data-testid="dl-template" {...lock(!!employer && has("employments.import_template"))}>↓ Template Excel</button>
          <button className="btn btn-secondary" onClick={()=>setImporting(true)} data-testid="btn-import" {...lock(!!employer && has("employments.import"))}>Import Excel</button>
          <button className="btn btn-primary" onClick={newE} data-testid="add-employment" {...lock(!!employer && has("employments.create"))}>+ Raport nou</button>
        </>} />
      <Toast msg={msg.text} type={msg.type} />
      {!loading && !employer && (
        <div className="card" style={{padding:16,marginBottom:16,borderLeft:"3px solid #DC2626",background:"#FEF2F2",fontSize:13}} data-testid="no-employer-banner">
          Trebuie definit mai întâi <b>Angajatorul</b> înainte de a introduce sau importa rapoarte de muncă. Mergi la pagina „Angajator".
        </div>
      )}
      <FilterForm f={ef} testid="empl-filters" style={{padding:14,marginBottom:12}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(170px, 1fr))",gap:10}}>
          <div><label className="label">Marcă</label>
            <input className="input" value={ef.draft.marca} onChange={e=>ef.set({marca:e.target.value})} data-testid="empl-filter-marca" /></div>
          <div><label className="label">Nume</label>
            <input className="input" value={ef.draft.last} onChange={e=>ef.set({last:e.target.value})} data-testid="empl-filter-last" placeholder="ex: Popescu" /></div>
          <div><label className="label">Prenume</label>
            <input className="input" value={ef.draft.first} onChange={e=>ef.set({first:e.target.value})} data-testid="empl-filter-first" placeholder="ex: Ana" /></div>
          <div><label className="label">Structură</label>
            <Select className="select" value={ef.draft.unit} onChange={e=>ef.set({unit:e.target.value})} data-testid="empl-filter-unit">
              <option value="">Toate structurile</option>
              {unitOptions.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </Select></div>
          <div><label className="label">Status</label>
            <Select className="select" value={ef.draft.status} onChange={e=>ef.set({status:e.target.value})} data-testid="empl-filter-status">
              <option value="activ_susp">Active și suspendate</option>
              <option value="active">Active</option>
              <option value="suspended">Suspendate</option>
              <option value="terminated">Încetate</option>
              <option value="all">Toate (inclusiv încetate)</option>
            </Select></div>
        </div>
      </FilterForm>
      <div className="card">
        <table className="tbl">
          <thead><tr><th>Marcă</th><th>Nume și prenume</th><th>Structură</th><th>Funcție contractuală</th><th>Funcții (tip)</th><th>Categorie</th>{didacticLicensed && <><th>Specialitate medicală</th><th>Vechime în învățământ</th></>}<th className="tabular-nums">Total CO</th><th>Status</th><th></th></tr></thead>
          <tbody>{filteredRows.map(e=>{
            const u = users.find(x=>x.id===e.user_id);
            const ou = units.find(x=>x.id===e.org_unit_id);
            const total = e.co_year_available+e.co_carried_previous+e.co_extra_hazardous+e.co_extra_other;
            return (<tr key={e.id} data-testid={`empl-row-${e.marca}`}>
              <td style={{fontWeight:600}} className="tabular-nums">{e.marca}</td>
              <td>{u ? `${u.last_name} ${u.first_name}` : "—"}</td>
              <td>{ou?.name || "—"}</td>
              <td>{e.stat_function_name || "—"}</td>
              <td data-testid={`empl-fns-${e.marca}`}>
                <div style={{fontSize:11,fontWeight:700,color:"var(--blue-primary)"}}>{{executie:"De execuție",conducere:"De conducere",ambele:"Execuție + conducere"}[e.function_type||"executie"]}</div>
                {[["internal_function_name",""],["cor_function_label",""],["salarizare_function_name",""]].map(([k])=>e[k] && <div key={k} style={{fontSize:11,color:"#64748B"}}>{e[k]}</div>)}
                {[["mgmt_internal_function_name"],["mgmt_cor_function_label"],["mgmt_salarizare_function_name"]].map(([k])=>e[k] && <div key={k} style={{fontSize:11,color:"#7C3AED"}}>Cond.: {e[k]}</div>)}
              </td>
              <td>{e.personnel_category}</td>
              {didacticLicensed && <>
                <td>{e.didactic ? [MEDICAL_LEVELS[e.didactic.medical_level] && e.didactic.medical_level ? MEDICAL_LEVELS[e.didactic.medical_level] : "", e.didactic.medical_specialty].filter(Boolean).join(", ") || "—" : "—"}</td>
                <td className="tabular-nums">{e.seniority?.higher_ed ? fmtTenure(e.seniority.higher_ed) : "—"}</td>
              </>}
              <td className="tabular-nums" style={{fontWeight:600}}>{total}z</td>
              <td>
                <span className={`badge ${STATUS_BADGE_CLASS[e.status] || "badge-inactive"}`}>{STATUS_LABELS[e.status] || e.status}</span>
                {e.status !== "active" && e.status_effective_date && <div style={{fontSize:11,color:"#64748B",marginTop:3}}>din {fmtDate(e.status_effective_date)}</div>}
                {e.pending_status && <div style={{fontSize:11,color:"#92400E",marginTop:3}} data-testid={`empl-pending-${e.marca}`}>Programat: {STATUS_LABELS[e.pending_status]} din {fmtDate(e.pending_status_date)}</div>}
              </td>
              <td>
                <RowActions testid={`row-menu-${e.marca}`} items={[
                  {label:"Vizualizează", icon:"eye", onClick:()=>setEditing({...e, view:true}), testid:`view-empl-${e.marca}`},
                  {label:"Editează", icon:"edit", onClick:()=>setEditing({...e}), testid:`edit-empl-${e.marca}`, ...lock(has("employments.update"))},
                ]}><PurgeButton entity="employment" id={e.id} onDone={load} /></RowActions>
              </td>
            </tr>);
          })}</tbody>
        </table>
        {rows.length===0 && <EmptyState text='Niciun raport de muncă. Folosește „Import Excel" sau „+ Raport nou".' />}
      </div>

      {importing && (
        <Modal title="Import Rapoarte de Muncă din Excel" onClose={()=>{setImporting(false); setImportResult(null);}} width={720}>
          {!importResult ? (
            <>
              <div style={{marginBottom:14,fontSize:13,color:"#334155"}}>
                <p style={{marginTop:0}}>1. Descarcă template-ul Excel cu structura corectă (butonul „↓ Template Excel").</p>
                <p>2. Completează datele. <b>Obligatorii</b> (marcate cu * în antet): marcă, nume, prenume, cod structură, funcția contractuală și <b>data aplicării CIM</b> (data începerii, apare în Raportul de muncă; nu se completează automat cu data importului); restul coloanelor sunt opționale (a doua foaie din template explică valorile implicite). Fișierele vechi cu o singură coloană „Funcție” rămân acceptate.</p>
                <p>3. <b>Utilizatorul se creează automat</b> din nume și prenume, dacă persoana nu există deja (după CNP sau utilizator) — de aceea coloanele „Utilizator” și „Parolă” sunt opționale. Coloana „Acces în platformă (Da/Nu)” spune dacă persoana primește cont de autentificare; necompletată = acces doar dacă ai dat parolă sau e-mail. Parola dată ignoră regulile de complexitate; utilizatorii o schimbă la prima logare.</p>
                <p>4. Template-ul are, după coloanele de bază, coloane opționale pentru datele ReGES ale angajatului și ale raportului, salariu și câmpurile personalizate definite la tine; a doua foaie spune ce valori se acceptă. O persoană găsită după CNP primește doar valorile completate pe rând (celulele goale nu șterg nimic).</p>
                <p>5. Alege fișierul, apoi apasă <b>Importă</b>.</p>
              </div>
              <button className="btn btn-secondary" onClick={downloadTemplate}>↓ Descarcă Template Excel</button>
              <ImportFileStep accept=".xlsx" onImport={uploadFile} testId="import-file" />
            </>
          ) : (
            <div>
              <div style={{padding:14,background:"var(--tint-100)",borderRadius:6,marginBottom:14}}>
                <div style={{fontWeight:700,color:"var(--navy-3)",marginBottom:6}}>Import finalizat</div>
                <div style={{fontSize:13}}>Rapoarte create: <b>{importResult.created_employments}</b></div>
                <div style={{fontSize:13}}>Utilizatori noi: <b>{importResult.created_users}</b></div>
                <div style={{fontSize:13}}>Utilizatori legați (existenți): <b>{importResult.linked_users}</b></div>
                {(importResult.created_functions||[]).length > 0 && (
                  <div style={{fontSize:13,marginTop:6}}>Intrări noi create automat în nomenclatoare: <b>{importResult.created_functions.length}</b>
                    <div style={{fontSize:12,color:"#334155",maxHeight:90,overflow:"auto"}}>
                      {importResult.created_functions.map((f,i)=><div key={i}>{f.kind}: {f.name}{f.code?` (cod ${f.code})`:""}</div>)}
                    </div>
                  </div>)}
              </div>
              {(importResult.ignored||[]).length > 0 && (
                <div style={{padding:12,background:"#FEF3C7",borderRadius:6,marginBottom:14,fontSize:13,color:"#92400E"}} data-testid="import-ignored">
                  <b>Coloane ignorate</b>
                  {importResult.ignored.map((c,i)=><div key={i}>{c.column} — {c.reason}</div>)}
                </div>
              )}
              {importResult.errors.length > 0 && (
                <div>
                  <div style={{fontWeight:700,color:"#991B1B",marginBottom:6}}>Erori ({importResult.errors.length})</div>
                  <div style={{maxHeight:240,overflow:"auto",border:"1px solid #FCA5A5",borderRadius:6}}>
                    <table className="tbl" style={{fontSize:12}}>
                      <thead><tr><th>Rând</th><th>Eroare</th></tr></thead>
                      <tbody>{importResult.errors.map((e,i)=>(<tr key={i}><td className="tabular-nums">{e.row}</td><td>{e.error}</td></tr>))}</tbody>
                    </table>
                  </div>
                </div>
              )}
              <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
                <button className="btn btn-primary" onClick={()=>{setImporting(false);setImportResult(null);}}>Închide</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {editing && (
        <Modal title={editing.view?"Raport de muncă — vizualizare":editing.id?"Editează raport de muncă":"Raport de muncă nou"} onClose={()=>setEditing(null)} width={780}>
          <FormTabs value={tab} onChange={setTab} tabs={[
            {id:"general", label:"Date generale", warn: !editing.user_id || !editing.marca?.trim() || !editing.org_unit_id},
            {id:"functie", label:"Funcția"},
            {id:"contract", label:"Date Contract și ReGES"},
            {id:"custom", label:"Câmpuri personalizate", hidden: customDefs.filter(d=>d.active).length === 0 && inherited.length === 0},
            {id:"events", label:"Evenimente", hidden: !editing.id},
            {id:"didactic", label:"Date didactice", hidden: !didacticLicensed},
            {id:"co", label:"Concediu de odihnă", hidden: !planCoLicensed},
            {id:"documente", label:"Documente", hidden: !(statFunctiiLicensed && editing.id && has("job_descriptions.view"))},
          ]} />
          <ViewOnly on={editing.view}>
          {(users.length === 0 || units.length === 0) && (
            <div style={{padding:12, background:"#FEF3C7", color:"#92400E", borderRadius:6, marginBottom:14, fontSize:13}}>
              {users.length === 0 && <div>⚠️ Nu există utilizatori. Creează întâi utilizatori în „Utilizatori & Roluri".</div>}
              {units.length === 0 && <div>⚠️ Nu există structuri. Creează întâi structuri în „Structură Organizatorică".</div>}
            </div>
          )}
          <div className="grid-2">
            <div style={tabStyle(tab,"general")}><div style={{gridColumn:"1/-1"}}><label className="label label-req">Angajat</label>
              <Select className="select" value={editing.user_id} onChange={e=>setEditing({...editing,user_id:e.target.value})} data-testid="empl-user">
                <option value="">— alege angajatul —</option>
                {users.filter(u=>u.active).map(u=><option key={u.id} value={u.id}>{u.last_name} {u.first_name}{u.cnp?` (CNP: ${u.cnp})`:""}</option>)}
              </Select></div>
            <div><label className="label label-req">Marcă</label>
              <input className="input" value={editing.marca} onChange={e=>setEditing({...editing,marca:e.target.value})} data-testid="empl-marca" /></div>
            </div>
            <div style={tabStyle(tab,"functie")}>
            <div style={{gridColumn:"1/-1",fontSize:12,fontWeight:700,color:"#334155",marginTop:4}}>Funcția</div>
            {fnSelect("stat_function_id","stat","Funcție contractuală","empl-fn-stat")}
            <div style={{gridColumn:"1/-1"}}>
              <div className="label">Tip funcție</div>
              <div style={{display:"flex",gap:18,flexWrap:"wrap"}} role="radiogroup" data-testid="empl-function-type">
                {[["executie","Funcție de execuție"],["conducere","Funcție de conducere"],["ambele","Ambele"]].map(([v,l])=>(
                  <label key={v} style={{display:"flex",gap:6,alignItems:"center",fontSize:13,cursor:"pointer"}}>
                    <input type="radio" name="function_type" checked={(editing.function_type||"executie")===v} data-testid={`empl-type-${v}`}
                      onChange={()=>setEditing({...editing,function_type:v})} />{l}
                  </label>
                ))}
              </div>
            </div>
            {(editing.function_type||"executie") !== "conducere" && <>
              {(editing.function_type||"executie") === "ambele" && <div style={{gridColumn:"1/-1",fontSize:12,fontWeight:700,color:"#334155",marginTop:4}}>Funcții de execuție</div>}
              {fnSelect("internal_function_id","internal",(editing.function_type||"executie")==="ambele"?"Funcție de execuție internă":"Funcție internă","empl-fn-internal",false,(editing.function_type==="ambele")?"propagate_internal":undefined)}
              {fnSelect("cor_function_id","cor",(editing.function_type||"executie")==="ambele"?"Funcție de execuție cod COR":"Funcție cod COR","empl-fn-cor",true,(editing.function_type==="ambele")?"propagate_cor":undefined)}
              {fnSelect("salarizare_function_id","salarizare",(editing.function_type||"executie")==="ambele"?"Funcție de execuție Legea salarizării":"Funcție Legea salarizării","empl-fn-salarizare",false,(editing.function_type==="ambele")?"propagate_salarizare":undefined)}
            </>}
            {(editing.function_type||"executie") !== "executie" && <>
              {(editing.function_type||"executie") === "ambele" && <div style={{gridColumn:"1/-1",fontSize:12,fontWeight:700,color:"#334155",marginTop:4}}>Funcții de conducere</div>}
              {fnSelect("mgmt_internal_function_id","management_internal",(editing.function_type==="ambele")?"Funcție de conducere internă":"Funcție internă","empl-fn-mgmt-internal",false,(editing.function_type==="ambele")?"propagate_mgmt_internal":undefined)}
              {fnSelect("mgmt_cor_function_id","cor",(editing.function_type==="ambele")?"Funcție de conducere cod COR":"Funcție cod COR","empl-fn-mgmt-cor",true,(editing.function_type==="ambele")?"propagate_mgmt_cor":undefined)}
              {fnSelect("mgmt_salarizare_function_id","management_salarizare",(editing.function_type==="ambele")?"Funcție de conducere Legea salarizării":"Funcție Legea salarizării","empl-fn-mgmt-salarizare",false,(editing.function_type==="ambele")?"propagate_mgmt_salarizare":undefined)}
            </>}
            </div>
            <div style={tabStyle(tab,"general")}>
            <div><label className="label label-req">Structură organizatorică</label>
              <Select className="select" value={editing.org_unit_id} onChange={e=>setEditing({...editing,org_unit_id:e.target.value})} data-testid="empl-unit">
                <option value="">— alege structură —</option>
                {units.filter(u=>u.active).map(u=><option key={u.id} value={u.id}>N{u.level} · {u.name}</option>)}
              </Select></div>
            <div><label className="label">Punct de lucru</label>
              <Select className="select" value={editing.location_id||""} onChange={e=>setEditing({...editing,location_id:e.target.value})} data-testid="empl-location">
                <option value="">— moștenește de la structură —</option>
                {locations.filter(l=>l.active).map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
              </Select></div>
            <div><label className="label">Categorie personal</label>
              <Select className="select" value={editing.personnel_category} onChange={e=>setEditing({...editing,personnel_category:e.target.value})}>
                {(cfg?.personnel_categories||["TESA"]).map(c=><option key={c}>{c}</option>)}
              </Select></div>
            </div>
            <div style={tabStyle(tab,"contract")} data-testid="empl-reges-tab">
              <div style={{gridColumn:"1/-1",fontSize:12,fontWeight:700,color:"#334155"}}>Contract</div>
              <RegesFields kind="employment" values={editing} prefix="er" keys={["reges_contract_type","reges_duration"]} onChange={patch=>setEditing(e=>({...e,...patch}))} />
              <div><label className="label">Număr CIM</label>
                <input className="input" value={editing.contract_number||""} onChange={e=>setEditing({...editing,contract_number:e.target.value})} data-testid="empl-contract-number" /></div>
              <RegesFields kind="employment" values={editing} prefix="er" keys={["registration_date"]} onChange={patch=>setEditing(e=>({...e,...patch}))} />
              <div><label className="label label-req">Dată început CIM</label>
                <input className="input" type="date" value={editing.start_date||""} onChange={e=>setEditing({...editing,start_date:e.target.value})} data-testid="empl-start-date" />
                <div style={{fontSize:11,color:"#64748B"}}>Data aplicării contractului (a intrării în activitate)</div></div>
              <div><label className="label">Dată sfârșit CIM</label>
                <input className="input" type="date" value={editing.end_date||""} disabled={!!editing.end_date_exception}
                  onChange={e=>setEditing({...editing,end_date:e.target.value})} data-testid="empl-end-date" />
                {editing.end_date_exception && <div style={{fontSize:11,color:"#64748B"}}>Blocată: ai ales o excepție de la data de sfârșit</div>}</div>
              <RegesFields kind="employment" values={editing} prefix="er" keys={["end_date_exception","details"]}
                onChange={patch=>setEditing(e=>({...e,...patch, ...(patch.end_date_exception ? { end_date: "" } : {})}))} />
              <SeniorityFields value={senForm} info={senInfoLive} onChange={setSenForm} />
              <RegesFields kind="employment" values={editing} prefix="er" only={["Timp de muncă"]} onChange={patch=>setEditing(e=>({...e,...patch}))} />
              <div style={{gridColumn:"1/-1",fontSize:12,fontWeight:700,color:"#334155",marginTop:6}}>Loc de muncă, studii și încadrare</div>
              <RegesFields kind="employment" values={editing} prefix="er" keys={["workplace_type","workplace_county","workplace_locality"]} onChange={patch=>setEditing(e=>({...e,...patch}))} />
              <div><label className="label">Nivel de studii (L153)</label>
                <Select className="select" value={editing.studii||""} onChange={e=>setEditing({...editing,studii:e.target.value})} data-testid="empl-studii">
                  <option value="">— nespecificat —</option>
                  {(cfg?.education_levels||[]).map(c=><option key={c}>{c}</option>)}
                </Select></div>
              <RegesFields kind="employment" values={editing} prefix="er" keys={["reges_education"]} onChange={patch=>setEditing(e=>({...e,...patch}))} />
              <div><label className="label">Gradație</label>
                <Select className="select" value={editing.gradatie ?? ""} onChange={e=>setEditing({...editing,gradatie:e.target.value})} data-testid="empl-gradatie">
                  <option value="">—</option>
                  {(nom?.gradatii||[]).map(g=><option key={g.value} value={g.value}>{g.label}</option>)}
                </Select></div>
              <div><label className="label">Grad / Treaptă</label>
                <input className="input" value={editing.grad_treapta||""} placeholder="ex: I, II, III, debutant" maxLength={64}
                  onChange={e=>setEditing({...editing,grad_treapta:e.target.value})} data-testid="empl-grad-treapta" /></div>
              <RegesFields kind="employment" values={editing} prefix="er" keys={["education_tranche"]} onChange={patch=>setEditing(e=>({...e,...patch}))} />
              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:20}}>
                <input type="checkbox" checked={!!editing.cumul_pensie} onChange={e=>setEditing({...editing,cumul_pensie:e.target.checked})} id="cumul_pensie" data-testid="empl-cumul-pensie" />
                <label htmlFor="cumul_pensie" className="label" style={{margin:0}}>Cumul pensie</label>
              </div>
              {pay && (
                <>
                  <div style={{gridColumn:"1/-1",fontSize:12,fontWeight:700,color:"#334155",marginTop:6}}>Salariu</div>
                  <div><label className="label">Salariu de bază brut</label>
                    <input className="input" type="number" min="0" step="0.01" value={pay.base_salary} onChange={e=>{setPay({...pay,base_salary:e.target.value});setPayDirty(true);}} data-testid="empl-pay" /></div>
                  <div><label className="label">Moneda</label>
                    <Select className="select" value={pay.currency} onChange={e=>{setPay({...pay,currency:e.target.value});setPayDirty(true);}} data-testid="empl-pay-currency">
                      <option value="RON">RON</option><option value="EUR">EUR</option><option value="USD">USD</option>
                    </Select></div>
                </>)}
            </div>
            <div style={tabStyle(tab,"general")}>
            {(() => {
              const orig = rows.find(r => r.id === editing.id);
              const origStatus = orig?.status || "active";
              const changing = editing.status !== origStatus;
              const todayIso = new Date().toISOString().slice(0, 10);
              const pick = (v) => setEditing({ ...editing, status: v,
                status_effective_date: v === origStatus ? (orig?.status_effective_date || null) : todayIso,
                ...(v === "active" ? { status_reason: "" } : {}) });
              const title = { suspended: "Suspendare CIM", terminated: "Încetare CIM", active: "Reactivare CIM" }[editing.status];
              return (<>
                <div><label className="label label-req">Status</label>
                  <Select className="select" value={editing.status||"active"} onChange={e=>pick(e.target.value)} data-testid="empl-status">
                    {Object.entries(STATUS_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
                  </Select>
                  <div style={{fontSize:11,color:"#64748B",marginTop:4}}>„Încetat" nu intră în facturare și nu apare ca opțiune activă în alte formulare.</div>
                </div>
                {orig?.pending_status && (
                  <div style={{gridColumn:"1/-1",background:"#FEF3C7",borderRadius:6,padding:"8px 12px",fontSize:12.5,color:"#92400E"}} data-testid="empl-pending-banner">
                    Schimbare programată: <b>{STATUS_LABELS[orig.pending_status]}</b> din <b>{fmtDate(orig.pending_status_date)}</b>
                    {orig.pending_status_reason ? ` — ${orig.pending_status_reason}` : ""}. Se aplică automat în ziua respectivă.{" "}
                    <label style={{marginLeft:8,fontWeight:600}}><input type="checkbox" checked={!!editing.cancel_pending_status}
                      onChange={e=>setEditing({...editing,cancel_pending_status:e.target.checked})} data-testid="empl-cancel-pending" /> Anulează programarea</label>
                  </div>
                )}
                {(changing || editing.status !== "active") && (
                  <div style={{gridColumn:"1/-1",border:"1px solid #E2E8F0",borderRadius:8,padding:"10px 12px",background:"#F8FAFC"}} data-testid="empl-status-section">
                    <div style={{fontSize:12,fontWeight:700,color:"#334155",marginBottom:8}}>{title}</div>
                    <div className="grid-2">
                      <div><label className="label">{editing.status==="active" ? "Data reactivării" : "Data de la care produce efecte"}</label>
                        {changing
                          ? <input className="input" type="date" value={editing.status_effective_date||""} onChange={e=>setEditing({...editing,status_effective_date:e.target.value})} data-testid="empl-status-date" />
                          : <div style={{fontSize:13,padding:"7px 0"}}>{editing.status_effective_date ? fmtDate(editing.status_effective_date) : "—"}</div>}
                      </div>
                      {editing.status !== "active" && (
                        <div><label className="label">{editing.status==="terminated" ? "Motiv încetare" : "Motiv suspendare"}</label>
                          <input className="input" value={editing.status_reason||""} onChange={e=>setEditing({...editing,status_reason:e.target.value})} data-testid="empl-status-reason"
                            placeholder={editing.status==="terminated" ? "ex: demisie, acord al părților..." : "ex: concediu creștere copil, concediu medical..."} /></div>
                      )}
                    </div>
                    <div style={{fontSize:11.5,color:"#64748B",marginTop:6,lineHeight:1.5}}>
                      {changing && "Dacă data este în viitor, schimbarea se aplică automat în ziua respectivă. "}
                      {editing.status==="terminated" && "Încetarea ultimului CIM al persoanei îi inactivează contul. În Stat de funcții postul devine vacant, iar CIM-ul trece la „fostul ocupant”."}
                      {editing.status==="suspended" && "Contul rămâne activ. În Stat de funcții postul devine „temporar vacant”, iar CIM-ul rămâne afișat."}
                      {editing.status==="active" && changing && "Contul se reactivează dacă fusese inactivat la încetarea CIM-ului."}
                    </div>
                  </div>
                )}
              </>);
            })()}
            </div>
            <div style={tabStyle(tab,"custom")} data-testid="empl-custom-tab">
              <CustomFields entity="employment" defs={customDefs} values={cfVals} inherited={inherited}
                onChange={(key,v)=>setCfVals(o=>({...o,[key]:v}))} prefix="ecf" />
            </div>
            <div style={tabStyle(tab,"didactic")} data-testid="empl-didactic-tab">
            {didacticLicensed && editing.didactic && (() => {
              const d = editing.didactic;
              const setD = (patch) => setEditing({ ...editing, didactic: { ...d, ...patch } });
              return (<>
                <div style={{gridColumn:"1/-1",fontSize:12,fontWeight:700,color:"#334155"}}>
                  Date didactice <span style={{fontWeight:400,color:"#64748B"}}>(Stat de Funcții Didactic)</span></div>
                <div><label className="label">Funcția didactică de încadrare</label>
                  <Select className="select" value={d.didactic_function||""} onChange={e=>setD({didactic_function:e.target.value})} data-testid="didactic-function">
                    {Object.entries(DIDACTIC_FUNCTIONS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
                  </Select></div>
                <div><label className="label">Nivelul medical deținut</label>
                  <Select className="select" value={d.medical_level} onChange={e=>setD({medical_level:e.target.value})} data-testid="didactic-medical-level">
                    {Object.entries(MEDICAL_LEVELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
                  </Select></div>
                <div><label className="label">Specialitatea medicală deținută</label>
                  <input className="input" value={d.medical_specialty} onChange={e=>setD({medical_specialty:e.target.value})} data-testid="didactic-medical-specialty" /></div>
                <div><label className="label">Calitatea de student doctorand / titlul științific de doctor</label>
                  <Select className="select" value={d.doctoral_status} onChange={e=>setD({doctoral_status:e.target.value, ...(e.target.value==="" ? {doctoral_domain:"", phd_supervisor:false} : {}), ...(e.target.value==="student_doctorand" ? {phd_supervisor:false} : {})})} data-testid="didactic-doctoral-status">
                    {Object.entries(DOCTORAL_STATUSES).map(([k,l])=><option key={k} value={k}>{l}</option>)}
                  </Select></div>
                <div><label className="label">Domeniul doctoratului</label>
                  <input className="input" disabled={!d.doctoral_status} value={d.doctoral_domain} onChange={e=>setD({doctoral_domain:e.target.value})} data-testid="didactic-doctoral-domain" /></div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <input type="checkbox" id="phd_supervisor" disabled={d.doctoral_status!=="doctor"} checked={!!d.phd_supervisor} onChange={e=>setD({phd_supervisor:e.target.checked})} data-testid="didactic-phd-supervisor" />
                  <label htmlFor="phd_supervisor" className="label" style={{margin:0}}>Conducător de doctorat</label></div>
                <div><label className="label">Mod ocupare post</label>
                  <Select className="select" value={d.occupation_mode} onChange={e=>setD({occupation_mode:e.target.value})} data-testid="didactic-occupation-mode">
                    {Object.entries(OCCUPATION_MODES).map(([k,l])=><option key={k} value={k}>{l}</option>)}
                  </Select></div>
                <div><label className="label">Reducere de normă pentru funcție de conducere (%)</label>
                  <input className="input" type="number" min="0" max="100" value={d.management_norm_reduction_percent} onChange={e=>setD({management_norm_reduction_percent:parseInt(e.target.value,10)||0})} data-testid="didactic-reduction" /></div>
                {editing.seniority && (
                  <div style={{gridColumn:"1/-1",fontSize:12,color:"#475569",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8,padding:"8px 12px"}} data-testid="didactic-seniority-readonly">
                    <b>Vechimi ale persoanei</b> (calculate până la {editing.seniority.reference_date}; se editează în fila „Date Contract și ReGES”):
                    {" "}în muncă <b>{fmtTenure(editing.seniority.work)}</b>
                    {" · "}în învățământ <b>{fmtTenure(editing.seniority.higher_ed)}</b>
                    {" · "}la angajator <b>{fmtTenure(editing.seniority.employer)}</b>{editing.seniority.employer?.auto ? " (de la primul contract)" : ""}
                    {" · "}în departament <b>{fmtMonths(editing.seniority.department_months)}</b>
                  </div>)}
              </>);
            })()}
            </div>
            <div style={tabStyle(tab,"co")}>
            {planCoLicensed && CO_FIELDS.map(([k,l])=>(
              <div key={k}><label className="label">{l}</label>
                <input className="input" type="number" value={editing[k]||0} onChange={e=>setEditing({...editing,[k]:parseInt(e.target.value)||0})} data-testid={`empl-${k}`} /></div>
            ))}
            </div>
            <div style={tabStyle(tab,"general")}>
            <div style={{gridColumn:"1/-1"}}><label className="label">Manager direct (Raport de muncă)</label>
              <Select className="select" value={editing.manager_employment_id||""} onChange={e=>setEditing({...editing,manager_employment_id:e.target.value})} data-testid="empl-manager">
                <option value="">— fără manager —</option>
                {rows.filter(r=>r.id!==editing.id && r.status !== "terminated").map(r=>(
                  <option key={r.id} value={r.id}>{r.marca} · {r.user_name || ""}</option>
                ))}
              </Select></div>
            <div style={{gridColumn:"1/-1"}}><label className="label">Acest raport înlocuiește un raport suspendat?</label>
              <Select className="select" value={editing.replaces_employment_id||""} onChange={e=>setEditing({...editing,replaces_employment_id:e.target.value})} data-testid="empl-replaces">
                <option value="">— nu —</option>
                {rows.filter(r=>r.id!==editing.id && r.status==="suspended").map(r=>(
                  <option key={r.id} value={r.id}>{r.marca} · {r.user_name || ""}</option>
                ))}
              </Select></div>
            {statFunctiiLicensed && !editing.replaces_employment_id && (
              <div style={{gridColumn:"1/-1"}}>
                <label style={{display:"flex",gap:8,alignItems:"center",fontSize:13,fontWeight:600}}>
                  <input type="checkbox" checked={editing._link_post ?? !!editing.post_id} data-testid="empl-link-post"
                    onChange={e=>setEditing({...editing,_link_post:e.target.checked,...(e.target.checked ? {} : {post_id:""})})} />
                  Leagă raportul de muncă de un post din Stat de funcții
                </label>
                {(editing._link_post ?? !!editing.post_id) && (
                  <div style={{marginTop:8}}><label className="label label-req">Post (Stat de funcții)</label>
                    <Select className="select" value={editing.post_id||""} onChange={e=>setEditing({...editing,post_id:e.target.value})} data-testid="empl-post">
                      <option value="">— alege post vacant/temporar vacant —</option>
                      {posts.filter(p=>POST_SELECTABLE_STATUSES.includes(p.status) || p.id===editing.post_id).map(p=>{
                        const unit = units.find(u=>u.id===p.org_unit_id);
                        return <option key={p.id} value={p.id}>{p.code} · {unit?.name||""} ({p.status_label})</option>;
                      })}
                    </Select>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
          {planCoLicensed && tab === "co" && (
            <div style={{marginTop:14,padding:10,background:"var(--tint-50)",borderRadius:6,fontSize:13,fontWeight:700,color:"var(--navy-3)"}}>
              Total zile CO: {editing.co_year_available+editing.co_carried_previous+editing.co_extra_hazardous+editing.co_extra_other}
            </div>
          )}
          </ViewOnly>
          {tab === "events" && editing.id && (
            <EmploymentEvents employmentId={editing.id} readOnly={!!editing.view}
              onApplied={patch => { setEditing(e => ({ ...e, ...patch, status_effective_date: patch.status_effective_date || "" })); load(); }} />
          )}
          {tab === "documente" && (
              <div data-testid="empl-docs">
                <div style={{fontSize:13,fontWeight:700,color:"var(--navy)",marginBottom:6}}>Fișa de post</div>
                <p style={{fontSize:13,color:"#475569",margin:"0 0 10px"}}>
                  {jdInfo.exists ? "Raportul are o fișă de post salvată." : "Raportul nu are încă o fișă de post."}
                  {editing.post_id ? (jdInfo.postHas ? " Postul din Stat de funcții are fișă: o poți prelua." : " Postul legat nu are încă fișă în Stat de funcții.") : " Raportul nu e legat de un post din Stat de funcții."}
                </p>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button type="button" className="btn btn-secondary" onClick={()=>nav(`/fisa-post/raport/${editing.id}`)} data-testid="empl-jd-open">{jdInfo.exists ? "Editează fișa de post" : "Redactează fișa de post"}</button>
                  {editing.post_id && has("job_descriptions.edit") && <button type="button" className="btn btn-secondary" onClick={()=>nav(`/fisa-post/raport/${editing.id}?preia=1`)} data-testid="empl-jd-preia">Preia fișa de post din Stat de funcții</button>}
                  <button type="button" className="btn btn-blue" onClick={async()=>{ try { await downloadFile(`/stat-functii/job-descriptions/employment/${editing.id}/pdf`, "fisa_post.pdf"); } catch(e){ setMsg({text:e.message,type:"error"}); } }} data-testid="empl-jd-pdf">Generează PDF</button>
                </div>
                <div style={{fontSize:11.5,color:"#64748B",marginTop:10,lineHeight:1.5}}>
                  Fișa se generează în PDF la cerere, cu datele raportului (CIM, ocupant, manager); nu se salvează în Dosarul electronic — acolo se încarcă fișa semnată și scanată.
                  Salvează modificările raportului înainte de a deschide fișa.
                </div>
              </div>
          )}
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:14}}>
            <button className="btn btn-secondary" onClick={()=>setEditing(null)}>{editing.view?"Închide":"Anulează"}</button>
            {!editing.view && <button className="btn btn-primary" onClick={save} data-testid="save-empl" {...lock(has(editing.id?"employments.update":"employments.create"))}>Salvează</button>}
          </div>
        </Modal>
      )}
    </>
  );
}
